import React, { useEffect, useState, useRef } from "react";
import { Box } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { CustomSelect, CustomTextField } from "../../../../../../shared/components/inputfields/form-elements";
import { EditableColumnDef, EditableTable } from "../../../../../../shared/components/table/inline-edit-table";
import { useFormActions } from "../../../../../../shared/hooks/form-builder.hooks";
import { ITcProductRawMaterialInformation as ImportedITcProductRawMaterialInformation } from "../../../../tc-models/tc-req-dto/tc-certified-product-details.req_dto";

interface ITcProductRawMaterialInformation extends ImportedITcProductRawMaterialInformation {
  tcProductNo?: string;
  additionalProp1?: any;
}
 
import { dtrackItService } from "../../../../tc-service/dtrackit.service";
import useNotification from "../../../../../../shared/hooks/useNotification";
import { useTransactionCertificateStore } from "../../../../../../store/transaction-certificates.store";
import { Utills } from "../../../../../../shared/app-configs/utilit";
import { useApi } from "../../../../../../shared/hooks/useApi";
import { TCAppConfig } from "../../../../../../shared/app-configs/tc-app-configs";
import { StyledButton } from "../../../../../../shared/components/buttons/custom-button";


interface RawMaterialsInfoProps {
  rawMaterialInfoData: ITcProductRawMaterialInformation[]; // Expecting an array
  setRawMaterialInfoData: React.Dispatch<
    React.SetStateAction<ITcProductRawMaterialInformation[]>
  >;
  selectedInputTcId: string;
  selectedProductIds: string[];
  isTC?: boolean | null;
}

const RawMaterialDetails: React.FC<RawMaterialsInfoProps> = ({
  rawMaterialInfoData,
  setRawMaterialInfoData,
  selectedInputTcId,
  selectedProductIds,
  isTC,
}) => {
  // CRITICAL FIX: Normalize function to handle all possible field name variations
  const normalizeItem = (item: any): ITcProductRawMaterialInformation => {    
    const normalized = {
      tcProductNo: item.tcProductNo || "",
      tcProductRawMaterialOrder: item.tcProductRawMaterialOrder || "",
      tcProductRawMaterialCode: item.tcProductRawMaterialCode || "",
      tcProductRawMaterialPercentage: item.tcProductRawMaterialPercentage?.toString() || "",
      tcProductRawMaterialUserSpecificTerm: item.tcProductRawMaterialUserSpecificTerm || "",
      additionalProp1: item.additionalProp1 || {}
    };    
    return normalized;
  };

  const [data, setData] = useState<ITcProductRawMaterialInformation[]>(() => {
    const normalized = rawMaterialInfoData.map(normalizeItem);
    return normalized;
  });
  const [tableData, setTableData] = useState<ITcProductRawMaterialInformation[]>(() => {
    return rawMaterialInfoData.map(normalizeItem);
  });
  const [editingRowIndex, setEditingRowIndex] = useState<number>(-1);
  const { updateField } = useFormActions("input-tc-product-details");
  const [rawMaterials, setRawMaterials] = useState([
    { value: "TEST001", label: "Test Cotton Fiber (TEST001)" },
    { value: "TEST002", label: "Test Organic Cotton (TEST002)" },
    { value: "TEST003", label: "Test Recycled Polyester (TEST003)" },
    { value: "TEST004", label: "Test Hemp Fiber (TEST004)" },
    { value: "TEST005", label: "Test Bamboo Fiber (TEST005)" },
    { value: "TEST006", label: "Test Wool Fiber (TEST006)" },
    { value: "TEST007", label: "Test Silk Fiber (TEST007)" },
    { value: "TEST008", label: "Test Linen Fiber (TEST008)" },
    { value: "TEST009", label: "Test Modal Fiber (TEST009)" },
    { value: "TEST010", label: "Test Viscose Fiber (TEST010)" }
  ]);
  const { notify } = useNotification()
  const { isRestrictPopUpSave, setRestrictPopUpSave } = useTransactionCertificateStore();
  const { executeGet } = useApi();
  const hasFetchedRef = useRef(false);
  const validationSchema = Yup.object().shape({
    tcProductRawMaterialCode: Yup.string().nullable(),
    tcProductRawMaterialPercentage: Yup.number()
      .typeError("Percentage must be a number")
      .positive("Percentage must be positive")
      .max(100, "Percentage cannot exceed 100")
      .test(
        'percentage-equals-100',
        'percentage-equals-100',
        function (value: any) {
          if (editingRowIndex > -1) {
            let total = data.reduce((sum, item, index) => {
              if (index === editingRowIndex) return sum;
              return sum + parseFloat(item.tcProductRawMaterialPercentage || "0");
            }, 0);
            if ((total + value) > 100) {
              notify("Raw Material Percentage cannot be greater than 100", "error");
              return false;
            }
          }
          return true;

        }
      )
      .nullable(),
    tcProductRawMaterialUserSpecificTerm: Yup.string().nullable(),
  }).test(
    'at-least-one',
    'At least one field is required',
    function (values) {
      return values.tcProductRawMaterialCode || values.tcProductRawMaterialPercentage
        ? true
        : this.createError({ message: 'At least one field is required' });
    }
  );
 
  useEffect(() => {
    const getRawMaterialCodes = async () => {
      try {
        const result: any = await dtrackItService.getRawMaterialCodes();
        const rawMaterials = result.data.map((t: any) => ({
          value: t.code_1,
          label: `${t.description_1} (${t.code_1})`
        }));
        setRawMaterials(rawMaterials);
      } catch (err) {
        Utills.cle(`Error in Fetching Organic Farm Standard Code ${err}`)
      }
    };
    getRawMaterialCodes();
  }, []);
 
  // FIX: Update parent whenever data changes
  useEffect(() => {
    const updatedData = data.map((item, index) => normalizeItem({
      ...item,
      tcProductRawMaterialOrder: (index + 1).toString()
    }));    
    updateField("raw-materials-info", updatedData);
    setRawMaterialInfoData(updatedData);
  }, [data]);
 
  useEffect(() => {
    hasFetchedRef.current = false;
  }, [selectedInputTcId, selectedProductIds]);
 
  // FIX: Better data loading from props and API
  useEffect(() => {
    const fetchRawMaterialDetails = async () => {
      if (rawMaterialInfoData && rawMaterialInfoData.length > 0) {        
        // Check each item for the field
        rawMaterialInfoData.forEach((item, idx) => {
        });
        
        const normalized = rawMaterialInfoData.map(normalizeItem);        
        setData(normalized);
        setTableData(normalized);
        return;
      }
      // Otherwise fetch from API
      if (!selectedInputTcId || selectedProductIds.length === 0 || hasFetchedRef.current) {
        return;
      }
 
      hasFetchedRef.current = true;

    }

  })

  // Reset fetch flag when parameters change
  useEffect(() => {
    hasFetchedRef.current = false;
  }, [selectedInputTcId, selectedProductIds]);

  // Fetch raw material details when component mounts and we have the required data
  useEffect(() => {
    const fetchRawMaterialDetails = async () => {
      // Don't call TC-related APIs if ATC is selected
      if (isTC === false) {
        return;
      }
      
      // Skip API call if we already have data (edit flow)
      if (rawMaterialInfoData && rawMaterialInfoData.length > 0) {
        setData(rawMaterialInfoData);
        setTableData(rawMaterialInfoData);
        return;
      }

      if (!selectedInputTcId || selectedProductIds.length === 0 || hasFetchedRef.current) {
        return;
      }

      hasFetchedRef.current = true;

      try {
        // Build URL with multiple productIds parameters
        const baseUrl = `${TCAppConfig.GET_STD_DETAILS_PDCT_INFO}?tcId=${selectedInputTcId}`;
        const productIdsParams = selectedProductIds.map(id => `&productIds=${id}`).join('');
        const url = `${baseUrl}${productIdsParams}`;
        const response: any = await executeGet(url);

        if (response?.data && Array.isArray(response.data)) {
          // Map the response data to our interface format
          const mappedData: ITcProductRawMaterialInformation[] = [];
          
          response.data.forEach((productData: any) => {
            if (productData.tcProductRawMaterialInformation && Array.isArray(productData.tcProductRawMaterialInformation)) {
              productData.tcProductRawMaterialInformation.forEach((rawMaterialInfo: any) => {
                mappedData.push({
                  tcProductNo: productData.tcProductNo || "",
                  tcProductRawMaterialOrder: rawMaterialInfo.tcProductRawMaterialOrder || "",
                  tcProductRawMaterialCode: rawMaterialInfo.tcProductRawMaterialCode || "",
                  tcProductRawMaterialPercentage: rawMaterialInfo.tcProductRawMaterialPercentage?.toString() || "",
                  tcProductRawMaterialUserSpecificTerm: rawMaterialInfo.tcProductRawMaterialUserSpecificTerm || "",
                });
              });
            }
          });

          // Update the data state
          setData(mappedData);
          setTableData(mappedData);
          setRawMaterialInfoData(mappedData);
        }
      } catch (err) {
        console.error("Failed to fetch raw material details:", err);
        notify("Failed to fetch raw material details", "error");
        hasFetchedRef.current = false; // Reset on error so it can retry
      }
    };

    fetchRawMaterialDetails();
  }, [selectedInputTcId, selectedProductIds, rawMaterialInfoData]);

  const formik = useFormik<ITcProductRawMaterialInformation>({
    initialValues: {
      tcProductNo: "",
      tcProductRawMaterialOrder: "",
      tcProductRawMaterialCode: "",
      tcProductRawMaterialPercentage: "",
      tcProductRawMaterialUserSpecificTerm: "",
      additionalProp1: {}
    },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {},
  });

    const handleAddNewRow = () => {
    const newRow: ITcProductRawMaterialInformation = {
      tcProductRawMaterialOrder: "",
      tcProductRawMaterialCode: "",
      tcProductRawMaterialPercentage: "",
      tcProductRawMaterialUserSpecificTerm: "",
    };
    setData((prevData) => [...prevData, newRow]);
    setEditingRowIndex(data.length);
    setRestrictPopUpSave(true)
    formik.setValues(newRow);
    formik.setTouched({});
  };
 
  const handleCellChange = (
    rowIndex: number,
    columnId: string,
    value: any
  ) => {
    
    // Update formik immediately
    formik.setFieldValue(columnId, value);
    
    // Update data state
    setData((prevData) =>
      prevData.map((row, index) => {
        if (index === rowIndex) {
          const updated = {
            ...row,
            [columnId]: value,
            tcProductRawMaterialOrder: (index + 1).toString()
          };
          return updated;
        }
        return row;
      })
    );
  };


  const handleCancelEdit = async () => {
    if (editingRowIndex !== -1) {
      setData(tableData);
    }
    setEditingRowIndex(-1);
    setRestrictPopUpSave(false);
    formik.resetForm();
  };

  const handleSaveRow = () => {
    if (editingRowIndex === -1 || !formik.isValid) return;
    
    if (!formik.values.tcProductRawMaterialCode || !formik.values.tcProductRawMaterialPercentage) {
      notify("Raw Material Code and Percentage are required", "error");
      return;
    } 
    // Create updated row with ALL fields explicitly
    const updatedRow: ITcProductRawMaterialInformation = {
      tcProductNo: formik.values.tcProductNo || "",
      tcProductRawMaterialOrder: (editingRowIndex + 1).toString(),
      tcProductRawMaterialCode: formik.values.tcProductRawMaterialCode || "",
      tcProductRawMaterialPercentage: formik.values.tcProductRawMaterialPercentage || "",
      tcProductRawMaterialUserSpecificTerm: formik.values.tcProductRawMaterialUserSpecificTerm || "",
      additionalProp1: formik.values.additionalProp1 || {}
    };

    // Update data array - preserve ALL fields for all rows
    const newData = data.map((row, index) => {
      if (index === editingRowIndex) {
        return updatedRow;
      }
      // Preserve all fields for non-edited rows
      return normalizeItem({
        ...row,
        tcProductRawMaterialOrder: (index + 1).toString()
      });
    });
  
    setData(newData);
    setTableData(newData);
    setRawMaterialInfoData(data); // Update parent with current data
    setEditingRowIndex(-1);
    setRestrictPopUpSave(false);
    formik.resetForm();
    notify("Row saved successfully", "success");
  };

 
  const columns: EditableColumnDef<ITcProductRawMaterialInformation>[] = [
    {
      accessorKey: "tcProductNo",
      header: "Product No",
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="tcProductNo"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter Product No"
          size="small"
        />
      ),
    },
    {
      accessorKey: "tcProductRawMaterialCode",
      header: "Raw Material Code",
      renderEditCell: ({ value, onChange }) => (
        <CustomSelect
          label=""
          name="tcProductRawMaterialCode"
          value={value || ''}
          options={rawMaterials}
          placeholder="Select Raw Material Code"
          enableSearch={true}
          onChange={(e) => onChange(e.target.value)}
        />
      ),
    },
    {
      accessorKey: "tcProductRawMaterialPercentage",
      header: "Raw Material Percentage",
      renderEditCell: ({ value, onChange }) => (
       
        <CustomTextField
          name="tcProductRawMaterialPercentage"
          value={value || ''}
          inputProps={{ max: 100 }}
          type="number"
          onChange={(e) => {
            const newValue = e.target.value;
            if (!newValue || (Number(newValue) <= 100 && /^[0-9]*$/.test(newValue))) {
              onChange(newValue);
            }
          }}
          placeholder="Enter value"
          sx={{ mb: 2 }}
        />
      ),
    },
    {
      accessorKey: "tcProductRawMaterialUserSpecificTerm",
      header: "User Specific Term",
      // FIX: Make sure the cell displays the value correctly
      Cell: ({ row }) => {
        const value = row.original.tcProductRawMaterialUserSpecificTerm;
        return <span>{value || '-'}</span>;
      },
      renderEditCell: ({ value, onChange }) => {
        return (
          <CustomTextField
            name="tcProductRawMaterialUserSpecificTerm"
            value={value || ''}
            onChange={(e) => {
              onChange(e.target.value);
            }}
            placeholder="Enter user specific term"
            size="small"
          />
        );
      },
    },
  ];
 
  const handleEditRow = (rowIndex: number) => {
    
    const row = data[rowIndex];    
    setEditingRowIndex(rowIndex);
    setRestrictPopUpSave(true);
    
    formik.setValues({
      tcProductNo: row.tcProductNo || "",
      tcProductRawMaterialOrder: row.tcProductRawMaterialOrder || "",
      tcProductRawMaterialCode: row.tcProductRawMaterialCode || "",
      tcProductRawMaterialPercentage: row.tcProductRawMaterialPercentage || "",
      tcProductRawMaterialUserSpecificTerm: row.tcProductRawMaterialUserSpecificTerm || "",
      additionalProp1: row.additionalProp1 || {}
    });
    
  };
 
  const handleDeleteRow = (rowIndex: number) => {
    const updatedData = data
      .filter((_, index) => index !== rowIndex)
      .map((row, index) => normalizeItem({
        ...row,
        tcProductRawMaterialOrder: (index + 1).toString()
      }));
    
    setData(updatedData);
    setTableData(updatedData);
    setRawMaterialInfoData(updatedData); // Update parent with updated data
  };
 
  return (
    <div>
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 1, border: "2px solid rgba(128, 128, 128, .2)", my: 4 }}
      >
        {/* <Box display="flex" mb={3} justifyContent="flex-end" width="100%">
          <StyledButton disabled={isRestrictPopUpSave}  onClick={handleAddNewRow} sx={{ mb: 2 }}>
            Add New Row
          </StyledButton>
        </Box> */}
        <EditableTable
          data={data}
          columns={columns}
          onSaveRow={handleSaveRow}
          onCellChange={handleCellChange}
          editingRowIndex={editingRowIndex}
          onEditRow={handleEditRow}
          onCancelEdit={handleCancelEdit}
          onDeleteRow={handleDeleteRow}
          enableEditing={true}
          hideDelete
        />
      </Box>
    </div>
  );
};

export default RawMaterialDetails;



